/* eslint-disable react/prop-types */
import { ApiClient } from '@api/api-configuration';
import { Client } from '@api/READ_ONLY/client_api/Api';
import { captureError } from '@app/lib/Sentry';
import ClientSelectGeneric from '@feat-clients/components/ClientSelect';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomComponentInputs } from '../filters.types';

/**
 * Client/Patient select wrapper for query filter system
 * Handles both single and multiple selection modes
 * Fetches client objects when IDs are provided from URL
 */
export const ClientFilterSelect: React.FC<CustomComponentInputs> = ({ onChange, value }) => {
  const { t } = useTranslation();
  const [loadedClients, setLoadedClients] = useState<Client[]>([]);

  // Normalize value to always be an array for mode="multiple"
  const normalizedValue = useMemo(() => {
    if (!value) return undefined;
    if (Array.isArray(value)) {
      return value.map((v) => Number(v));
    }
    return [Number(value)];
  }, [value]);

  // Fetch clients by IDs when value is provided (e.g., from URL)
  useEffect(() => {
    const fetchClientsByIds = async (ids: number[]) => {
      try {
        const clients = await Promise.all(
          ids.map(async (id) => {
            try {
              const response = await ApiClient.clientApi.clients.getClientById(id);
              return response.data;
            } catch (error) {
              captureError(error instanceof Error ? error : new Error(`Error fetching client ID ${id}`), {
                clientId: id,
                component: 'ClientFilterSelect',
              });
              return null;
            }
          })
        );
        const filtered = clients.filter((c) => c !== null) as Client[];
        setLoadedClients(filtered);
      } catch (error) {
        captureError(error instanceof Error ? error : new Error('Error fetching clients'), {
          clientIds: ids,
          component: 'ClientFilterSelect',
        });
      }
    };

    if (normalizedValue && normalizedValue.length > 0) {
      fetchClientsByIds(normalizedValue);
    } else {
      setLoadedClients([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedValue]);

  const handleChange = (client: Client | Client[] | undefined) => {
    if (Array.isArray(client)) {
      // Multiple selection - return array of IDs
      onChange(client.map((c) => c.clientId));
    } else if (client) {
      // Single selection - return single ID
      onChange(client.clientId);
    } else {
      // Cleared
      onChange(null);
    }
  };

  return (
    <ClientSelectGeneric
      allowClear={true}
      mode="multiple"
      placeholder={t('filters-select-clients-placeholder')}
      maxTagCount="responsive"
      onClientChange={handleChange}
      value={normalizedValue}
      selectedItemsProvider={() => (loadedClients.length > 0 ? loadedClients : undefined)}
      style={{ width: '100%', height: '44px' }}
      className="h-8"
    />
  );
};
